import { Form, Icon, TableColumn } from '@servicetitan/design-system';
import strings from './strings';
import { strings as errorStrings } from '@components/ErrorComponent/strings';
import type { GridPageChangeEvent } from '@progress/kendo-react-grid';
import React, { ChangeEvent, useState } from 'react';
import {
  DrawerTable,
  DrawerTableErrorWrapper,
  DrawerTableWrapper,
} from '../Drawer/DrawerTable.styled';
import formatAddress from '@lib/utils/formatAddress';
import ErrorComponent from '@components/ErrorComponent/ErrorComponent';
import DrawerHeader from '../Drawer/DrawerHeader/DrawerHeader';
import LocationNameCell from './components/LocationNameCell';
import { Drawer } from '@components/Drawer';
import { containsAllWordsInInput } from './containsAllWordsInInput';
import { locationLinkToMonolith } from '@lib/utils/tenantLinks';
import useLazyCustomerLocationsQuery, {
  CustomerLocation,
  CustomerLocationFetcher,
} from './data/useLazyCustomerLocationsQuery';
import styles from './LocationsDrawer.module.css';

const TABLE_ROW_HEIGHT = 47;
const PAGE_SIZE = 20;
const MAX_QUERY_BATCH_SIZE = 5000; // enforced by the server

interface TableData {
  address: string;
  locationLinkToMonolith: string;
  name: string | null | undefined;
}

interface TableState {
  skip: number;
  take: number;
}

type LoadAllLocationsResult =
  | {
      locations: CustomerLocation[];
      isLoading: false;
      error: undefined;
    }
  | {
      locations: undefined;
      isLoading: true;
      error: undefined;
    }
  | {
      locations: undefined;
      isLoading: false;
      error: unknown;
    };

/**
 * Transforms the customer locations to the table data.
 * @param customerLocations The customer locations.
 * @param tenantId The tenant ID.
 * @returns The table data.
 */
const transformCustomerLocations = (
  customerLocations: CustomerLocation[],
  tenantId: string,
): TableData[] => {
  return customerLocations.map((location) => ({
    address: formatAddress(location.address),
    locationLinkToMonolith: locationLinkToMonolith(tenantId, location.id),
    name: location.name,
  }));
};

/**
 * Filters the table data by the search input.
 * @param tableData The table data.
 * @param searchInput The search input.
 * @returns The filtered table data.
 */
const filterTableData = (
  tableData: TableData[],
  searchInput: string,
): TableData[] => {
  return tableData.filter((data) =>
    containsAllWordsInInput(data.address + data.name || '', searchInput),
  );
};

/**
 * Loads all customer locations, including the total count.
 * @returns The customer locations
 */
const loadAllLocations = async ({
  tenantId,
  customerId,
  fetchCustomerLocations,
}: {
  tenantId: string;
  customerId: string;
  fetchCustomerLocations: CustomerLocationFetcher;
}): Promise<CustomerLocation[]> => {
  const outputLocations: CustomerLocation[] = [];

  let totalCount: number | undefined;
  do {
    const includeTotalCount = totalCount == null;
    const locationBatch = await fetchCustomerLocations({
      tenantId,
      customerId,
      skip: outputLocations.length,
      take: MAX_QUERY_BATCH_SIZE,
      includeTotalCount,
    });

    if (includeTotalCount) totalCount = locationBatch.totalCount as number;
    outputLocations.push(...locationBatch.data);
  } while (totalCount != null && outputLocations.length < totalCount);

  return outputLocations;
};

export interface LocationsDrawerProps {
  open: boolean;
  customerId: string;
  tenantId: string;
  onClose: () => void;
}

export const LocationsDrawer = (props: LocationsDrawerProps) => {
  const { customerId, tenantId, open, onClose } = props;
  const { lastError: error, fetchCustomerLocations } =
    useLazyCustomerLocationsQuery();

  const [locationSearchInput, setLocationInput] = useState('');
  const [loadAllLocationsResult, setLoadAllLocationsResult] =
    useState<LoadAllLocationsResult>({
      isLoading: true,
      error: undefined,
      locations: undefined,
    });
  const [tableState, setTableState] = useState<TableState>({
    skip: 0,
    take: PAGE_SIZE,
  });

  const transformedTableData = React.useMemo(() => {
    if (!loadAllLocationsResult) return [];
    const { locations } = loadAllLocationsResult;
    if (!locations) return [];
    return transformCustomerLocations(locations, tenantId);
  }, [loadAllLocationsResult, tenantId]);

  const filteredTableData = React.useMemo(() => {
    return filterTableData(transformedTableData, locationSearchInput);
  }, [transformedTableData, locationSearchInput]);

  const noResults =
    filteredTableData.length === 0 &&
    !loadAllLocationsResult.isLoading &&
    !error;

  const windowedTableData = React.useMemo(() => {
    return filteredTableData.slice(
      tableState.skip,
      tableState.skip + tableState.take,
    );
  }, [filteredTableData, tableState]);

  // show pagination if there are any results
  const showPagination = filteredTableData.length > 0;

  const onPageChange = async (ev: GridPageChangeEvent): Promise<void> => {
    const { page } = ev;
    const { take, skip } = page;
    setTableState({ skip, take });
  };

  const onRetry = () => {
    setLoadAllLocationsResult({
      isLoading: true,
      error: undefined,
      locations: undefined,
    });
    loadAllLocations({
      tenantId,
      customerId,
      fetchCustomerLocations,
    })
      .then((locations) => {
        setLoadAllLocationsResult({
          locations,
          isLoading: false,
          error: undefined,
        });
      })
      .catch((error) => {
        setLoadAllLocationsResult({
          locations: undefined,
          isLoading: false,
          error,
        });
      });
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setLocationInput(e.target.value);
    setTableState((t) => ({ ...t, skip: 0 }));
  };

  // initial load all locations
  React.useEffect(() => {
    onRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, tenantId]);

  return (
    <Drawer
      onClose={onClose}
      header={
        <DrawerHeader
          title={strings.labels.header}
          count={loadAllLocationsResult.locations?.length ?? 0}
        />
      }
      open={open}
    >
      <div className={styles.locationsDrawerContent}>
        <Form.Input
          className='m-b-2'
          shortLabel={<Icon name='search' />}
          small
          placeholder={strings.placeholders.search}
          value={locationSearchInput}
          onChange={onSearchChange}
        />
        <DrawerTableWrapper $flex={1} $overflowHidden>
          <DrawerTable
            $height='100%'
            loading={loadAllLocationsResult.isLoading}
            pageable={
              showPagination
                ? {
                    buttonCount: 3,
                  }
                : false
            }
            scrollable='scrollable'
            striped={false}
            skip={tableState.skip}
            pageSize={tableState.take}
            data={windowedTableData}
            rowHeight={TABLE_ROW_HEIGHT}
            onPageChange={onPageChange}
            total={filteredTableData.length}
          >
            <TableColumn
              field={'name'}
              title={strings.labels.nameColumn}
              width='170'
              locked
              cell={LocationNameCell}
            />
            <TableColumn
              field='address'
              title={strings.labels.addressColumn}
              locked
            />
          </DrawerTable>
          {(error || noResults) && (
            <DrawerTableErrorWrapper>
              <ErrorComponent
                image={noResults ? 'block' : undefined}
                title={noResults ? errorStrings.noResultsHeadline : undefined}
                body={
                  error
                    ? errorStrings.retryMessage
                    : errorStrings.tryAnotherSearch
                }
                primaryActionLabel={
                  error ? errorStrings.labels.retry : undefined
                }
                primaryAction={error ? onRetry : undefined}
              />
            </DrawerTableErrorWrapper>
          )}
        </DrawerTableWrapper>
      </div>
    </Drawer>
  );
};
